import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Check, Clock, Delete, Lock, ShieldCheck, X } from 'lucide-react';
import {
  verifyManagerOverride,
  type ManagerOverrideAuthorizedBy,
} from '@/lib/api/pin.js';
import { overrideRequestsApi, type OverrideRequestDto } from '@/lib/api/override-requests.js';
import { getCurrentStore } from '@/lib/api/stores.js';
import { parseApiError } from '@/lib/api/errors.js';
import { cn } from '@/lib/utils.js';
import { prettyLabel } from '@/lib/format.js';
import { useQuery } from '@tanstack/react-query';

const REMOTE_DEFAULT_ACTIONS = new Set([
  'apply_discount',
  'price_override',
  'waive_deposit',
]);

const KEYPAD = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'back', '0', 'submit'] as const;

type Step = 'reason' | 'choose' | 'pin' | 'waiting' | 'denied' | 'expired' | 'success';

export function ManagerOverrideModal({
  open,
  onClose,
  onApproved,
  action,
  title,
  description,
  requiredRole = 'store_manager',
  context,
}: {
  open: boolean;
  onClose: () => void;
  onApproved: (authorizedBy: ManagerOverrideAuthorizedBy) => void;
  action: string;
  title: string;
  description: string;
  requiredRole?: 'store_manager' | 'tenant_admin';
  context?: {
    ticketId?: string;
    customerId?: string;
    amount?: number;
    percent?: number;
    reason?: string;
    [key: string]: unknown;
  };
}) {
  const [step, setStep] = useState<Step>('reason');
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);
  const [success, setSuccess] = useState<ManagerOverrideAuthorizedBy | null>(null);
  const [triedFourDigit, setTriedFourDigit] = useState(false);
  const [remoteReq, setRemoteReq] = useState<OverrideRequestDto | null>(null);
  const [denialReason, setDenialReason] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const pollRef = useRef<number | null>(null);

  const storeQuery = useQuery({
    queryKey: ['current-store'],
    queryFn: getCurrentStore,
    staleTime: 60_000,
    enabled: open,
  });

  const approvalSettings = (storeQuery.data?.approvalSettings ?? {}) as {
    enabled?: boolean;
    actionModes?: Record<string, string>;
  };
  const mode =
    approvalSettings.actionModes?.[action] ??
    (REMOTE_DEFAULT_ACTIONS.has(action) ? 'remote_or_pin' : 'pin_only');
  const remoteEnabled = Boolean(approvalSettings.enabled) && mode === 'remote_or_pin';

  useEffect(() => {
    if (!open) return;
    setStep(context?.reason?.trim() ? 'choose' : 'reason');
    setReason(context?.reason?.trim() ?? '');
    setPin('');
    setError('');
    setShake(false);
    setSuccess(null);
    setTriedFourDigit(false);
    setRemoteReq(null);
    setDenialReason('');
    setSecondsLeft(0);
    window.setTimeout(() => inputRef.current?.focus(), 50);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [open, context?.reason]);

  useEffect(() => {
    if (step !== 'waiting' || !remoteReq) return;
    const tick = () => {
      const left = Math.max(
        0,
        Math.ceil((new Date(remoteReq.expiresAt).getTime() - Date.now()) / 1000),
      );
      setSecondsLeft(left);
    };
    tick();
    const id = window.setInterval(tick, 500);
    return () => window.clearInterval(id);
  }, [step, remoteReq]);

  useEffect(() => {
    if (step !== 'waiting' || !remoteReq?.id) return;

    const poll = async () => {
      try {
        const fresh = await overrideRequestsApi.get(remoteReq.id);
        setRemoteReq(fresh);
        if (fresh.status === 'approved' && fresh.approvedBy) {
          if (pollRef.current) window.clearInterval(pollRef.current);
          const authorizedBy: ManagerOverrideAuthorizedBy = {
            id: fresh.approvedBy,
            firstName: (fresh.approvedByName ?? 'Manager').split(' ')[0] ?? 'Manager',
            lastName: (fresh.approvedByName ?? '').split(' ').slice(1).join(' ') || 'A',
            role: 'store_manager',
          };
          setSuccess(authorizedBy);
          setStep('success');
          window.setTimeout(() => onApproved(authorizedBy), 800);
        } else if (fresh.status === 'denied') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setDenialReason(fresh.denialReason ?? 'Denied');
          setStep('denied');
        } else if (fresh.status === 'expired' || fresh.status === 'cancelled') {
          if (pollRef.current) window.clearInterval(pollRef.current);
          setStep('expired');
        }
      } catch {
        /* keep polling */
      }
    };

    void poll();
    pollRef.current = window.setInterval(poll, 2000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [step, remoteReq?.id, onApproved]);

  const pinMutation = useMutation({
    mutationFn: (value: string) =>
      verifyManagerOverride({
        pin: value,
        action,
        requiredRole,
        context: {
          ...context,
          reason: reason.trim() || context?.reason,
        },
      }),
    onSuccess: (result) => {
      if (result.approved && result.authorizedBy) {
        setSuccess(result.authorizedBy);
        setStep('success');
        window.setTimeout(() => onApproved(result.authorizedBy!), 1000);
        return;
      }
      setError(
        result.reason === 'locked'
          ? 'Manager PIN is locked — try another manager'
          : 'Incorrect PIN — try again',
      );
      setShake(true);
      setPin('');
      window.setTimeout(() => setShake(false), 400);
    },
    onError: (err) => {
      setError(parseApiError(err).message);
      setShake(true);
      setPin('');
      window.setTimeout(() => setShake(false), 400);
    },
  });

  const requestMutation = useMutation({
    mutationFn: () =>
      overrideRequestsApi.create({
        action,
        requiredRole,
        reason: reason.trim(),
        context: {
          ...context,
          title,
          description,
          reason: reason.trim(),
        },
        ...(context?.ticketId ? { ticketId: context.ticketId } : {}),
        ...(context?.customerId ? { customerId: context.customerId } : {}),
      }),
    onSuccess: (data) => {
      setRemoteReq(data);
      setStep('waiting');
    },
    onError: (err) => setError(parseApiError(err).message),
  });

  const cancelMutation = useMutation({
    mutationFn: () => overrideRequestsApi.cancel(remoteReq!.id),
    onSuccess: () => onClose(),
    onError: () => onClose(),
  });

  if (!open) return null;

  function submitPin(value: string) {
    if (value.length < 4 || value.length > 6 || pinMutation.isPending || success) return;
    pinMutation.mutate(value);
  }

  function handleChange(next: string) {
    const digits = next.replace(/\D/g, '').slice(0, 6);
    setPin(digits);
    setError('');
    if (digits.length === 4 && !triedFourDigit) {
      setTriedFourDigit(true);
      submitPin(digits);
      return;
    }
    if (digits.length === 6) submitPin(digits);
  }

  function handleKeypad(key: (typeof KEYPAD)[number]) {
    if (key === 'back') {
      setPin((p) => p.slice(0, -1));
      setError('');
      return;
    }
    if (key === 'submit') {
      submitPin(pin);
      return;
    }
    handleChange(pin + key);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    submitPin(pin);
  }

  function handleKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    if (event.key === 'Escape') onClose();
  }

  const pinSlots = Math.max(4, Math.min(6, pin.length || 4));
  const canProceed = reason.trim().length > 0;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="manager-override-title"
      onClick={step === 'waiting' ? undefined : onClose}
    >
      <div
        className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {step === 'success' && success ? (
          <div className="py-8 text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
              <Check className="h-6 w-6" />
            </div>
            <p className="text-base font-bold text-slate-950">Approved</p>
            <p className="mt-1 text-sm text-slate-600">
              by {success.firstName} {success.lastName.charAt(0)}. ({prettyLabel(success.role)})
            </p>
          </div>
        ) : step === 'denied' ? (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-slate-950">Request denied</h2>
            <p className="rounded-xl bg-red-50 px-3 py-2 text-sm text-red-800">{denialReason}</p>
            <button
              type="button"
              className="w-full rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-white"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        ) : step === 'expired' ? (
          <div className="space-y-4">
            <h2 className="text-base font-bold text-slate-950">Request expired</h2>
            <p className="text-sm text-slate-600">No manager responded in time. Retry remotely or use a PIN.</p>
            <div className="flex gap-2">
              <button
                type="button"
                className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                onClick={() => {
                  setError('');
                  setStep('choose');
                }}
              >
                Retry
              </button>
              <button
                type="button"
                className="flex-1 rounded-xl bg-primary px-3 py-2 text-sm font-semibold text-white"
                onClick={() => {
                  setError('');
                  setStep('pin');
                }}
              >
                Use PIN
              </button>
            </div>
          </div>
        ) : step === 'waiting' ? (
          <div className="space-y-4 text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-amber-100 text-amber-800">
              <Clock className="h-6 w-6 animate-pulse" />
            </div>
            <h2 className="text-base font-bold text-slate-950">Waiting for approval…</h2>
            {remoteReq?.notifiedNames?.length ? (
              <p className="text-sm text-slate-600">
                Notified: {remoteReq.notifiedNames.join(', ')}
              </p>
            ) : (
              <p className="text-sm text-slate-600">Managers have been notified</p>
            )}
            <p className="text-sm font-semibold text-slate-800">
              Expires in {secondsLeft}s
            </p>
            <button
              type="button"
              className="w-full rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              Cancel Request
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="rounded-xl bg-amber-100 p-2 text-amber-800">
                  <ShieldCheck className="h-4 w-4" />
                </div>
                <div>
                  <h2 id="manager-override-title" className="text-base font-bold text-slate-950">
                    Manager Approval Required
                  </h2>
                  <p className="text-xs text-slate-500">{title}</p>
                </div>
              </div>
              <button
                type="button"
                className="rounded-lg p-1 text-slate-500 hover:bg-slate-100"
                onClick={onClose}
                aria-label="Cancel"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="mt-4 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-700">
              {description}
            </p>

            {(step === 'reason' || step === 'choose' || step === 'pin') && (
              <div className="mt-4">
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">
                  Reason <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Why is this needed?"
                  disabled={step === 'pin' && pinMutation.isPending}
                />
              </div>
            )}

            {error ? (
              <p className="mt-2 text-center text-sm font-semibold text-red-600">{error}</p>
            ) : null}

            {step === 'reason' ? (
              <button
                type="button"
                className="mt-4 w-full rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                disabled={!canProceed}
                onClick={() => setStep('choose')}
              >
                Continue
              </button>
            ) : null}

            {step === 'choose' ? (
              <div className="mt-4 grid gap-2">
                <button
                  type="button"
                  className="flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white disabled:opacity-50"
                  disabled={!canProceed}
                  onClick={() => {
                    if (!canProceed) return;
                    setStep('pin');
                    window.setTimeout(() => inputRef.current?.focus(), 50);
                  }}
                >
                  <Lock className="h-4 w-4" />
                  Manager PIN
                </button>
                {remoteEnabled ? (
                  <button
                    type="button"
                    className="rounded-xl border border-primary px-4 py-2.5 text-sm font-semibold text-primary disabled:opacity-50"
                    disabled={!canProceed || requestMutation.isPending}
                    onClick={() => {
                      if (!canProceed) return;
                      requestMutation.mutate();
                    }}
                  >
                    {requestMutation.isPending ? 'Sending…' : 'Request Approval'}
                  </button>
                ) : null}
                <button
                  type="button"
                  className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            ) : null}

            {step === 'pin' ? (
              <form className="mt-4" onSubmit={submit}>
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <Lock className="h-3.5 w-3.5" />A manager must enter their PIN
                </p>

                <input
                  ref={inputRef}
                  type="password"
                  inputMode="numeric"
                  autoComplete="off"
                  maxLength={6}
                  value={pin}
                  onChange={(e) => handleChange(e.target.value)}
                  onKeyDown={handleKeyDown}
                  className="sr-only"
                  aria-label="Manager PIN"
                />

                <div
                  className={cn(
                    'mb-3 flex justify-center gap-2',
                    shake && 'animate-[shake_0.35s_ease-in-out]',
                  )}
                >
                  {Array.from({ length: pinSlots }).map((_, i) => (
                    <div
                      key={i}
                      className={cn(
                        'flex h-11 w-10 items-center justify-center rounded-xl border text-lg font-bold',
                        pin[i]
                          ? 'border-primary bg-primary/5 text-primary'
                          : 'border-slate-300 bg-white text-transparent',
                      )}
                    >
                      •
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-3 gap-2">
                  {KEYPAD.map((key) => (
                    <button
                      key={key}
                      type="button"
                      className={cn(
                        'flex h-12 items-center justify-center rounded-xl text-lg font-semibold',
                        key === 'submit'
                          ? 'bg-primary text-white hover:bg-primary/90'
                          : 'bg-slate-100 text-slate-800 hover:bg-slate-200',
                      )}
                      onClick={() => handleKeypad(key)}
                      disabled={pinMutation.isPending || !canProceed}
                    >
                      {key === 'back' ? (
                        <Delete className="h-5 w-5" />
                      ) : key === 'submit' ? (
                        <Check className="h-5 w-5" />
                      ) : (
                        key
                      )}
                    </button>
                  ))}
                </div>

                <div className="mt-3 flex gap-2">
                  {remoteEnabled ? (
                    <button
                      type="button"
                      className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold"
                      onClick={() => setStep('choose')}
                    >
                      Back
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="flex-1 rounded-xl border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700"
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                </div>
              </form>
            ) : null}
          </>
        )}
      </div>

      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          20%, 60% { transform: translateX(-6px); }
          40%, 80% { transform: translateX(6px); }
        }
      `}</style>
    </div>
  );
}
